/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './Subscriptions.scss';

import { groupBy, isEqual, mapKeys, omit, uniq } from 'lodash';
import { array, bool, object } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import AccountAndFundAlerts from 'components/layouts/Preferences/Subscriptions/AccountAndFundAlerts';
import AdditionalNotifications from 'components/layouts/Preferences/Subscriptions/AdditionalNotifications';
import FixedSubmitFooter from 'components/layouts/Preferences/shared/FixedSubmitFooter/FixedSubmitFooter';
import If from 'components/shared/If/If';
import { InvalidOrMissingEmail } from 'components/layouts/Preferences/InvalidOrMissingEmail';
import {
  getMutualFundInformation,
  getCustomerSubscriptions,
  saveSubscriptions
} from 'services/Rest';
import {
  ALL_FUNDS_SUBSCRIPTION_TYPE_ID,
  DAILY_PRICES_TYPE_ID,
  getAccountFundAlertsSelectedType,
  MARKET_WRAP_UP_IDS,
  MONTHLY_FUND_TYPE_ID,
  MY_FUNDS_SUBSCRIPTION_TYPE_ID,
  OPT_OUT_CATEGORY_ID,
  optOutCategories,
  SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID
} from 'services/Preferences';

import {
  getListOfMutualFundGroups,
  getMutualFunds,
  getUserMutualFundsWithIVData
} from 'state/preferences/PreferencesSelectors';
import {
  getSubscriptions,
  getSubscriptionSaveStatus,
  getSubscriptionElections
} from 'state/subscriptions/SubscriptionsSelectors';
import { getView, isSalesforceRepEnabled } from 'state/view/ViewSelectors';
import { getPrimaryEmailAddress } from 'state/customer/CustomerSelectors';
import { getHasUpdateSubscriptions } from 'state/requests/RequestStatusSelectors';
import { getHasInvalidOrNoEmailAddress } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import { updateSubscriptions } from 'state/subscriptions/SubscriptionsActions';
import Store from 'state/Store';

class Subscriptions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showSave: false
    };
  }

  componentDidMount() {
    getMutualFundInformation();
    getCustomerSubscriptions();
  }

  componentDidUpdate(prevProps) {
    const {
      subscriptionsSave: {
        saveStatus: { lastUpdate, success }
      }
    } = this.props;
    const previousUpdate = prevProps.subscriptionsSave.saveStatus.lastUpdate;
    const currentUpdate = lastUpdate;
    const updatedSubscriptions = previousUpdate != currentUpdate;
    if (updatedSubscriptions && !success) {
      getCustomerSubscriptions();
    }
  }

  // use this but with a wrapper for each section to get the a appropriate sub object in the larger
  // subscriptions object
  onChange = (key, value) => {
    this.shouldShowSave(true);
    Store.dispatch(updateSubscriptions({ [key]: value }));
  };

  getFundsByRadioSelection = (typeId, selectedTrpCodes) => {
    const { mutualFunds, userMutualFundsWithIVData } = this.props;
    switch (typeId) {
      case MY_FUNDS_SUBSCRIPTION_TYPE_ID:
        return userMutualFundsWithIVData.map((mf) => mf.trpCode);
      case ALL_FUNDS_SUBSCRIPTION_TYPE_ID:
        // Again another strange technical requirement that if all funds is selected we will send null for trp codes and
        // all asset classes
        return null;
      case SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID:
        return uniq(
          selectedTrpCodes
            .map((code) => mutualFunds.find((mf) => mf.trpCode === code))
            .map((fund) => fund.trpCode)
        );
      default:
        return [];
    }
  };

  getBroadAssetsByRadioSelection = (typeId, selectedTrpCodes) => {
    const { mutualFunds } = this.props;
    switch (typeId) {
      case MY_FUNDS_SUBSCRIPTION_TYPE_ID:
        return [70];
      case ALL_FUNDS_SUBSCRIPTION_TYPE_ID:
        return [10, 20, 30, 40, 50];
      case SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID: {
        // this is very strange technical requirement that if not all funds are selected within an asset group we
        // will not send that asset group to the data base. If all funds are selected we will send all asset classes
        const allAssetGroups = groupBy(
          mutualFunds,
          (mf) => mf.broadAssetClassId
        );
        const selectedFundGroups = groupBy(
          selectedTrpCodes.map((code) =>
            mutualFunds.find((mf) => mf.trpCode === code)
          ),
          (mf) => mf.broadAssetClassId
        );
        const assetsForSubmission = [];
        mapKeys(selectedFundGroups, (value, key) => {
          const allFundsOfAssetClass = allAssetGroups[key];
          const allIncluded =
            allFundsOfAssetClass &&
            allFundsOfAssetClass.length === value.length &&
            allFundsOfAssetClass.every((fund) => value.includes(fund));
          if (allIncluded) {
            assetsForSubmission.push(parseInt(key));
          }
        });
        return assetsForSubmission;
      }
      default:
        return [];
    }
  };

  updateAccountFundAlerts = (key, value) => {
    this.shouldShowSave(true);
    const {
      subscriptions: {
        digitalSubscriptions: {
          newsLetterIds,
          priceSubscriptions,
          performanceSubscriptions
        }
      },
      view: { isTracOnly }
    } = this.props;
    let payload = {};

    if (key === 'marketWrapups') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            newsLetterIds: newsLetterIds.concat(MARKET_WRAP_UP_IDS)
          }
        };
      } else {
        // filter out market wrap up ids from state subscriptions
        const remainingNewsletters = newsLetterIds.filter(
          (id) => MARKET_WRAP_UP_IDS.indexOf(id) === -1
        );
        payload = {
          digitalSubscriptions: {
            newsLetterIds: remainingNewsletters
          }
        };
      }
    }
    if (key === 'taxForms') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            taxForms: true
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            taxForms: false
          }
        };
      }
    }

    const selectedTrpCodes = value['trpCodes'] ? value.trpCodes : [];

    const performanceTypeSelected = getAccountFundAlertsSelectedType(
      key,
      value,
      performanceSubscriptions,
      isTracOnly
    );
    const performanceCodes = this.getFundsByRadioSelection(
      performanceTypeSelected,
      selectedTrpCodes
    );
    const performanceAssetClasses = this.getBroadAssetsByRadioSelection(
      performanceTypeSelected,
      selectedTrpCodes
    );

    const priceTypeSelected = getAccountFundAlertsSelectedType(
      key,
      value,
      priceSubscriptions,
      isTracOnly
    );
    const priceCodes = this.getFundsByRadioSelection(
      priceTypeSelected,
      selectedTrpCodes
    );
    const priceAssetClasses = this.getBroadAssetsByRadioSelection(
      priceTypeSelected,
      selectedTrpCodes
    );

    const EMPTY_SELECTION = {
      typeId: -1,
      broadAssetClassIds: [],
      trpCodes: []
    };

    if (key === 'myFundsDailyPrices') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: {
              typeId: priceTypeSelected,
              broadAssetClassIds: priceAssetClasses,
              trpCodes: priceCodes
            },
            newsLetterIds: uniq(newsLetterIds.concat(DAILY_PRICES_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: EMPTY_SELECTION,
            newsLetterIds: newsLetterIds.filter(
              (id) => DAILY_PRICES_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'allFundsDailyPrices') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: {
              typeId: priceTypeSelected,
              broadAssetClassIds: priceAssetClasses,
              trpCodes: priceCodes
            },
            newsLetterIds: uniq(newsLetterIds.concat(DAILY_PRICES_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: EMPTY_SELECTION,
            newsLetterIds: newsLetterIds.filter(
              (id) => DAILY_PRICES_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'selectDailyFundPrices') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: {
              typeId: priceTypeSelected,
              broadAssetClassIds: priceAssetClasses,
              trpCodes: priceCodes
            },
            newsLetterIds: uniq(newsLetterIds.concat(DAILY_PRICES_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: EMPTY_SELECTION,
            newsLetterIds: newsLetterIds.filter(
              (id) => DAILY_PRICES_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'myFundsMonthlyFundPerformance') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: {
              typeId: performanceTypeSelected,
              broadAssetClassIds: performanceAssetClasses,
              trpCodes: performanceCodes
            },
            newsLetterIds: uniq(newsLetterIds.concat(MONTHLY_FUND_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: EMPTY_SELECTION,
            newsLetterIds: newsLetterIds.filter(
              (id) => MONTHLY_FUND_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'allFundsMonthlyFundPerformance') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: {
              typeId: performanceTypeSelected,
              broadAssetClassIds: performanceAssetClasses,
              trpCodes: performanceCodes
            },
            newsLetterIds: uniq(newsLetterIds.concat(MONTHLY_FUND_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: EMPTY_SELECTION,
            newsLetterIds: newsLetterIds.filter(
              (id) => MONTHLY_FUND_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'selectMonthlyFundPerformance') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: {
              typeId: performanceTypeSelected,
              broadAssetClassIds: performanceAssetClasses,
              trpCodes: performanceCodes
            },
            newsLetterIds: uniq(newsLetterIds.concat(MONTHLY_FUND_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: EMPTY_SELECTION,
            newsLetterIds: newsLetterIds.filter(
              (id) => MONTHLY_FUND_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'dailyFundPricesSelected') {
      if (!value) {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: EMPTY_SELECTION,
            newsLetterIds: newsLetterIds.filter(
              (id) => DAILY_PRICES_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'monthlyFundPerformancesSelected') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: {
              typeId: performanceTypeSelected,
              broadAssetClassIds: performanceAssetClasses,
              trpCodes: performanceCodes
            },
            newsLetterIds: uniq(newsLetterIds.concat(MONTHLY_FUND_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            newsLetterIds: newsLetterIds.filter(
              (id) => MONTHLY_FUND_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'dailyFundPricesSelected') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            priceSubscriptions: {
              broadAssetClassIds: priceAssetClasses,
              trpCodes: priceCodes,
              typeId: priceTypeSelected
            },
            newsLetterIds: uniq(newsLetterIds.concat(DAILY_PRICES_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            newsLetterIds: newsLetterIds.filter(
              (id) => DAILY_PRICES_TYPE_ID !== id
            )
          }
        };
      }
    }
    if (key === 'monthlyFundPerformanceSelected') {
      if (value) {
        payload = {
          digitalSubscriptions: {
            performanceSubscriptions: {
              broadAssetClassIds: performanceAssetClasses,
              trpCodes: performanceCodes,
              typeId: performanceTypeSelected
            },
            newsLetterIds: uniq(newsLetterIds.concat(MONTHLY_FUND_TYPE_ID))
          }
        };
      } else {
        payload = {
          digitalSubscriptions: {
            newsLetterIds: newsLetterIds.filter(
              (id) => MONTHLY_FUND_TYPE_ID !== id
            )
          }
        };
      }
    }
    Store.dispatch(updateSubscriptions(payload));
  };

  optStatus = (key, value) => {
    this.shouldShowSave(true);

    const {
      subscriptions: { optOutElections }
    } = this.props;

    const category = optOutCategories.find((c) => c.key === key);
    if (category) {
      const { categoryId, categoryName } = category;

      const payload = {
        optOutElections: [
          {
            categoryId,
            categoryName,
            optOut: !value
          }
        ]
      };

      if (key !== 'optOutAll') {
        const optOut = optOutElections.find(
          (o) => o.categoryId === OPT_OUT_CATEGORY_ID
        );
        if (optOutElections && optOut) {
          payload.optOutElections.push({
            categoryId: optOut.categoryId,
            categoryName: optOut.categoryName,
            optOut: true
          });
        }
      }

      if (key === 'optOutAll' && value) {
        optOutCategories
          .filter((o) => o.categoryId !== OPT_OUT_CATEGORY_ID)
          .forEach((ooe) => {
            payload.optOutElections.push({
              categoryId: ooe.categoryId,
              categoryName: ooe.categoryName,
              optOut: true
            });
          });
      }
      payload.optOutElections.forEach((el) =>
        Store.dispatch(updateSubscriptions({ optOutElections: el }))
      );
    }
  };

  fundSelectionChange = (key, value) => {
    this.shouldShowSave(true);
    this.setState({ [key]: value });
  };

  handleSave = () => {
    const { customerEmail, subscriptions } = this.props;

    let updatedSubscriptions = subscriptions;
    updatedSubscriptions.disposition = {
      action: 'update'
    };

    // document delivery should be the only updater of compliance subscriptions
    updatedSubscriptions = omit(updatedSubscriptions, [
      'complianceSubscriptions'
    ]);

    customerEmail.disposition = {};
    customerEmail.expirationDate = '';

    let payload = {
      emailAddress: customerEmail,
      subscriptions: updatedSubscriptions
    };

    saveSubscriptions(payload);
    this.shouldShowSave(false);
  };

  handleCancel = () => {
    const { history } = this.props;
    this.shouldShowSave(false);
    history.push('/profile');
  };

  shouldShowSave = (show) => {
    // this has a small flaw where if you back and forth between opt out subscriptions
    // although the values match because a new object may be added on opt in it is treated as difference
    // therefore will show sticky save until save of that new object is successful.
    const {
      subscriptions,
      subscriptionsSave: { subscriptionsFromDB }
    } = this.props;

    if (show) {
      this.setState({ showSave: show });
    } else {
      this.setState({ showSave: isEqual(subscriptions, subscriptionsFromDB) });
    }
  };

  render() {
    const {
      isSalesforceRepEnabled,
      hasInvalidOrMissingEmail,
      subscriptions,
      view
    } = this.props;

    const { showSave } = this.state;

    return (
      <Fragment>
        {!isSalesforceRepEnabled && (
          <PreferencesHeader>
            <div className="subscriptions-header">Subscriptions</div>
          </PreferencesHeader>
        )}
        <PreferencesSection
          className="subscriptions-section"
          fullBackground={true}>
          <div className="subscriptions-page">
            <If test={hasInvalidOrMissingEmail}>
              <InvalidOrMissingEmail page="subscription" view={view} />
            </If>
            <div className="subscriptions-container">
              <Fragment>
                <div className="subscriptions-column fill-column">
                  <AccountAndFundAlerts
                    subscriptions={subscriptions}
                    onChange={this.updateAccountFundAlerts}
                    onFundSelectionChange={this.fundSelectionChange}
                  />
                </div>
                <div className="subscriptions-column">
                  <AdditionalNotifications
                    subscriptions={subscriptions}
                    optStatus={this.optStatus}
                  />
                </div>
              </Fragment>
            </div>
            <FixedSubmitFooter
              cancel={this.handleCancel}
              shouldShow={showSave}
              submit={this.handleSave}
              shouldDisable
            />
          </div>
        </PreferencesSection>
      </Fragment>
    );
  }
}

Subscriptions.propTypes = {
  history: object.isRequired,
  listOfMutualFundGroups: array.isRequired,
  requestStatus: object.isRequired,
  subscriptionsElections: array.isRequired,
  hasInvalidOrMissingEmail: bool.isRequired
};

Subscriptions.defaultProps = {
  subscriptionsElections: []
};

const mapStateToProps = (state) => ({
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  hasUpdateSubscriptions: getHasUpdateSubscriptions(state),
  listOfMutualFundGroups: getListOfMutualFundGroups(state),
  mutualFunds: getMutualFunds(state),
  userMutualFundsWithIVData: getUserMutualFundsWithIVData(state),
  requestStatus: state.requestStatus,
  subscriptions: getSubscriptions(state),
  subscriptionsSave: getSubscriptionSaveStatus(state),
  subscriptionsElections: getSubscriptionElections(state),
  hasInvalidOrMissingEmail: getHasInvalidOrNoEmailAddress(state),
  customerEmail: getPrimaryEmailAddress(state),
  view: getView(state),
  router: state.router
});

export default withRouter(connect(mapStateToProps)(Subscriptions));
